import { Autocomplete, TextField, FormControl } from "@mui/material";
import type { ChangeEventHandler, FocusEventHandler } from "react";
import { useId } from "react";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
// import { Geo } from "@aws-amplify/geo";
import { useController, useFormContext } from "react-hook-form";
import { useTranslations } from "next-intl";
import type { FormInputProps } from "./FormInput";
import { getValidationRules } from "./utils/getValidationRules";
import { FormControlLabel } from "./FormControlLabel";
import { HelperMessage } from "./HelperMessage";

// https://mui.com/material-ui/react-autocomplete/#google-maps-place

type FormAutocompleteProps = FormInputProps;

export const FormAutocomplete: React.FC<FormAutocompleteProps> = ({
  id,
  className,
  disabled,
  formatter = (str) => str,
  label = "",
  name,
  onBlur,
  onFocus,
  onChange,
  placeholder,
  prefixIcon,
  required = true,
  rules = {},
  successMessage = "",
  shouldUnregister,
  showError,
  showValidationIcon = true,
}) => {
  const defaultId = useId();
  const t = useTranslations();

  // TODO (Sherif) finish autocomplete after IAM stuff is done
  // const addressOptions = Geo.searchByText("123 Rightmove");

  const addressOptions = [{ title: "123 Rightmove" }];

  const validationRules = getValidationRules({
    required,
    rules,
    t,
  });

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const {
    field: { onChange: onFieldChange, onBlur: onFieldBlur, value },
    fieldState: { error, isTouched },
  } = useController({
    name,
    rules: validationRules,
    control,
    shouldUnregister,
    disabled,
  });

  const hasValidation = required || Object.keys(rules).length > 0;
  const isSuccessIndicatorActive = isTouched && hasValidation;
  const shouldShowSuccessMessage =
    Boolean(isSuccessIndicatorActive) && !error && Boolean(successMessage);

  const validationIcon = !error ? (
    <CheckCircleOutlineOutlinedIcon className="fill-rmh-green-700" />
  ) : (
    <CancelOutlinedIcon className="fill-rmh-red-500" />
  );

  const suffixIcon = (
    <>
      {isSuccessIndicatorActive && showValidationIcon ? validationIcon : null}
    </>
  );

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    onFieldBlur();
    onBlur?.(e);
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const formattedValue = formatter(e.target.value);
    e.target.value = formattedValue;
    onFieldChange(e);
    onChange?.(e);
  };

  return (
    <FormControl className={className ?? "flex flex-1 flex-col"}>
      <FormControlLabel id={id ?? defaultId} label={label} />
      <Autocomplete
        autoComplete
        disableClearable
        freeSolo
        options={addressOptions.map((option) => option.title)}
        renderInput={(params) => (
          <TextField
            {...params}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              ...params.InputProps,
              startAdornment: prefixIcon,
              endAdornment: suffixIcon,
            }}
            disabled={disabled}
            error={Boolean(error)}
            id={id ?? defaultId}
            name={name}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={onFocus}
            placeholder={placeholder}
            size="small"
            sx={{
              ".MuiOutlinedInput-notchedOutline": {
                borderRadius: "8px",
              },
              ".MuiInputBase-root": {
                display: "flex",
                gap: "8px",
              },
            }}
            value={value ?? ""}
            variant="outlined"
          />
        )}
      />
      <HelperMessage
        errors={errors}
        message={successMessage}
        messageProps={{
          className: "body-m text-rmh-green-700",
          hidden: !shouldShowSuccessMessage,
        }}
        name={name}
        showError={showError}
      />
    </FormControl>
  );
};
